import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { GearSix, Coin, Flag, Megaphone, ArrowsClockwise, FloppyDisk, Trash, Plus, Lock, CheckCircle, XCircle, Eye, Scales, ArrowCounterClockwise, PersonSimpleRun, Trophy, FilePdf, Palette } from "@phosphor-icons/react";
import { useTheme } from "@/context/ThemeContext";
import { Navigate } from "react-router-dom";

const RULE_LABELS = {
  match_winner: "Match: Ganador (validado)",
  match_loser: "Match: Perdedor",
  reta_winner: "Reta: Ganador",
  reta_loser: "Reta: Perdedor",
  tournament_winner: "Torneo: Campeón",
  rental_per_50mxn: "Renta: por cada $50 MXN",
  atari_threshold_300: "Atari: +300 pts",
  registration_bonus: "Bono de registro",
};

const FLAG_LABELS = {
  streaming: "Streaming en vivo",
  trivia: "Trivia",
  atari: "Atari Tennis",
  ar_vestuario: "AR / Vestuario Virtual",
  dyd_coach: "DYD Coach (AI)",
  bets: "Apuestas DYD",
  shop: "Tienda DYD",
  topup: "Recargas con tarjeta",
  withdraw: "Retiros a banco",
};

export default function PlatformControl() {
  const { user } = useAuth();
  const [rules, setRules] = useState(null);
  const [flags, setFlags] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [newAnn, setNewAnn] = useState({ message: "", type: "info", url: "", expires_at: "" });
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [unlocks, setUnlocks] = useState([]);
  const [proofPreview, setProofPreview] = useState(null);
  const [legalDocs, setLegalDocs] = useState([]);
  const [editingLegal, setEditingLegal] = useState(null);  // {key, title, content}
  const [savingLegal, setSavingLegal] = useState(false);

  const load = async () => {
    try {
      const [r, f, a, u, l] = await Promise.all([
        api.get("/admin/dyd-coin/rules"),
        api.get("/admin/platform/feature-flags"),
        api.get("/admin/announcements"),
        api.get("/admin/dyd-coin/unlocks/pending").catch(() => ({ data: { pending: [] } })),
        api.get("/admin/legal").catch(() => ({ data: { documents: [] } })),
      ]);
      setRules(r.data.rules);
      setFlags(f.data.feature_flags);
      setAnnouncements(a.data.announcements || []);
      setUnlocks(u.data.pending || []);
      setLegalDocs(l.data.documents || []);
    } catch (e) {
      toast.error("Error al cargar configuración");
    }
  };
  useEffect(() => { load(); }, []);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const saveRules = async () => {
    try {
      await api.put("/admin/dyd-coin/rules", { rules });
      toast.success("Reglas DYD actualizadas");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const saveFlags = async () => {
    try {
      await api.put("/admin/platform/feature-flags", { feature_flags: flags });
      toast.success("Feature flags guardados");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const runBackfill = async () => {
    if (!window.confirm("Esto acreditará DYD retroactivos a TODOS los usuarios según su actividad histórica (idempotente). ¿Continuar?")) return;
    setBackfillRunning(true);
    setBackfillResult(null);
    try {
      const r = await api.post("/admin/dyd-coin/backfill", {});
      setBackfillResult(r.data);
      toast.success(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error backfill");
    } finally {
      setBackfillRunning(false);
    }
  };

  const createAnn = async () => {
    if (!newAnn.message.trim()) { toast.error("Mensaje requerido"); return; }
    try {
      await api.post("/admin/announcements", { ...newAnn, expires_at: newAnn.expires_at || null });
      toast.success("Anuncio creado");
      setNewAnn({ message: "", type: "info", url: "", expires_at: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const toggleAnn = async (a) => {
    try {
      await api.put(`/admin/announcements/${a.id}`, { active: !a.active });
      load();
    } catch (e) { toast.error("Error"); }
  };

  const deleteAnn = async (id) => {
    if (!window.confirm("¿Eliminar anuncio?")) return;
    try {
      await api.delete(`/admin/announcements/${id}`);
      toast.success("Eliminado");
      load();
    } catch (e) { toast.error("Error"); }
  };

  const viewProof = async (userId) => {
    try {
      const r = await api.get(`/admin/dyd-coin/unlocks/${userId}/proof`);
      if (r.data.url) setProofPreview(r.data.url);
      else setProofPreview(`${process.env.REACT_APP_BACKEND_URL}/api/storage/${r.data.path}`);
    } catch (e) {
      toast.error("Error al cargar comprobante");
    }
  };

  const approveUnlock = async (userId) => {
    if (!window.confirm("¿Aprobar y desbloquear esta wallet?")) return;
    try {
      await api.post(`/admin/dyd-coin/unlocks/${userId}/approve`);
      toast.success("Wallet aprobada");
      load();
    } catch (e) { toast.error("Error"); }
  };

  const rejectUnlock = async (userId) => {
    const reason = window.prompt("Motivo del rechazo:");
    if (!reason) return;
    try {
      await api.post(`/admin/dyd-coin/unlocks/${userId}/reject`, { reason });
      toast.success("Rechazado");
      load();
    } catch (e) { toast.error("Error"); }
  };

  const saveLegal = async () => {
    if (!editingLegal) return;
    setSavingLegal(true);
    try {
      await api.put(`/admin/legal/${editingLegal.key}`, {
        title: editingLegal.title,
        content: editingLegal.content,
      });
      toast.success(`${editingLegal.title} actualizado`);
      setEditingLegal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingLegal(false);
    }
  };

  const resetLegal = async (key) => {
    if (!window.confirm("¿Restaurar al texto predeterminado? Perderás los cambios actuales.")) return;
    try {
      await api.post(`/admin/legal/${key}/reset`);
      toast.success("Restaurado al default");
      setEditingLegal(null);
      load();
    } catch (e) { toast.error("Error"); }
  };

  return (
    <div className="space-y-4" data-testid="platform-control-page">
      <div className="card-tactical p-4" style={{ background: `linear-gradient(135deg, var(--theme-primary, #CCFF00)15 0%, transparent 100%)`, borderColor: "var(--theme-primary, #CCFF00)" }}>
        <p className="text-[10px] tracking-[0.3em] uppercase font-bold flex items-center gap-1" style={{ color: "var(--theme-primary, #CCFF00)" }}>
          <GearSix size={12} weight="fill" /> Control de Plataforma
        </p>
        <h1 className="font-['Barlow_Condensed'] text-4xl font-black text-white leading-none mt-1">disfrutaydiviertete.com</h1>
        <p className="text-xs text-gray-400 mt-1">Modifica reglas, módulos, tema y permisos sin tocar código.</p>
      </div>

      <Tabs defaultValue="rules">
        <TabsList className="bg-[#0A0A0A] border border-white/10">
          <TabsTrigger value="rules" data-testid="tab-rules"><Coin size={14} className="mr-1" /> Reglas DYD</TabsTrigger>
          <TabsTrigger value="flags" data-testid="tab-flags"><Flag size={14} className="mr-1" /> Feature Flags</TabsTrigger>
          <TabsTrigger value="anns" data-testid="tab-announcements"><Megaphone size={14} className="mr-1" /> Anuncios</TabsTrigger>
          <TabsTrigger value="backfill" data-testid="tab-backfill"><ArrowsClockwise size={14} className="mr-1" /> Backfill</TabsTrigger>
          <TabsTrigger value="unlocks" data-testid="tab-unlocks"><Lock size={14} className="mr-1" /> Activaciones {unlocks.length > 0 && <span className="ml-1 bg-amber-500 text-black px-1 rounded text-[9px] font-black">{unlocks.length}</span>}</TabsTrigger>
          <TabsTrigger value="legal" data-testid="tab-legal"><Scales size={14} className="mr-1" /> Legal</TabsTrigger>
          <TabsTrigger value="dyd-run" data-testid="tab-dyd-run"><PersonSimpleRun size={14} className="mr-1" /> DYD Run</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports"><FilePdf size={14} className="mr-1" /> Reportes</TabsTrigger>
          <TabsTrigger value="theme" data-testid="tab-theme"><Palette size={14} className="mr-1" /> Tema</TabsTrigger>
        </TabsList>

        {/* REGLAS */}
        <TabsContent value="rules" className="mt-3 space-y-3">
          <div className="card-tactical p-4">
            <p className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00] mb-3">Editor de reglas de auto-earn</p>
            {!rules ? <p className="text-sm text-gray-500">Cargando...</p> : (
              <div className="grid sm:grid-cols-2 gap-3">
                {Object.entries(rules).map(([k, v]) => (
                  <div key={k}>
                    <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">{RULE_LABELS[k] || k}</Label>
                    <Input type="number" min="0" max="10000" value={v}
                      onChange={(e) => setRules({ ...rules, [k]: Number(e.target.value) || 0 })}
                      className="bg-[#0A0A0A] border-white/15 mt-1 font-mono"
                      data-testid={`rule-${k}`} />
                  </div>
                ))}
              </div>
            )}
            <Button onClick={saveRules} className="mt-4 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="save-rules">
              <FloppyDisk size={14} className="mr-1" /> Guardar reglas
            </Button>
          </div>
        </TabsContent>

        {/* FLAGS */}
        <TabsContent value="flags" className="mt-3 space-y-3">
          <div className="card-tactical p-4">
            <p className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00] mb-3">Activar/desactivar módulos</p>
            {!flags ? <p className="text-sm text-gray-500">Cargando...</p> : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(flags).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between bg-[#0A0A0A] border border-white/10 px-3 py-2 rounded">
                    <span className="text-sm text-white">{FLAG_LABELS[k] || k}</span>
                    <Switch checked={!!v} onCheckedChange={(c) => setFlags({ ...flags, [k]: c })} data-testid={`flag-${k}`} />
                  </div>
                ))}
              </div>
            )}
            <Button onClick={saveFlags} className="mt-4 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="save-flags">
              <FloppyDisk size={14} className="mr-1" /> Guardar flags
            </Button>
            <p className="text-[10px] text-gray-500 mt-2">Los usuarios verán los cambios al recargar la página.</p>
          </div>
        </TabsContent>

        {/* ANUNCIOS */}
        <TabsContent value="anns" className="mt-3 space-y-3">
          <div className="card-tactical p-4 space-y-3 max-w-2xl">
            <p className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00]">Nuevo anuncio (banner global)</p>
            <Input value={newAnn.message} onChange={(e) => setNewAnn({ ...newAnn, message: e.target.value })}
              placeholder="Ej: ¡Torneo abierto! Inscríbete antes del 30 de marzo"
              className="bg-[#0A0A0A] border-white/15" data-testid="new-ann-message" maxLength={500} />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Tipo</Label>
                <Select value={newAnn.type} onValueChange={(v) => setNewAnn({ ...newAnn, type: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/15 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/15">
                    <SelectItem value="info">Info (azul)</SelectItem>
                    <SelectItem value="success">Éxito (verde)</SelectItem>
                    <SelectItem value="warn">Aviso (amarillo)</SelectItem>
                    <SelectItem value="promo">Promo (neon)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Expira (opcional)</Label>
                <Input type="datetime-local" value={newAnn.expires_at}
                  onChange={(e) => setNewAnn({ ...newAnn, expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                  className="bg-[#0A0A0A] border-white/15 mt-1" />
              </div>
            </div>
            <Input value={newAnn.url} onChange={(e) => setNewAnn({ ...newAnn, url: e.target.value })}
              placeholder="URL opcional (al hacer click)" className="bg-[#0A0A0A] border-white/15" />
            <Button onClick={createAnn} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="create-ann">
              <Plus size={14} className="mr-1" /> Crear anuncio
            </Button>
          </div>

          <div className="card-tactical p-0 overflow-hidden">
            <h3 className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00] p-3">Anuncios ({announcements.length})</h3>
            {announcements.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">Sin anuncios.</div>
            ) : (
              <div className="divide-y divide-white/5 max-h-[500px] overflow-y-auto">
                {announcements.map((a) => (
                  <div key={a.id} className="px-3 py-2 grid grid-cols-[1fr_90px_120px_80px] gap-2 items-center text-xs" data-testid={`ann-${a.id}`}>
                    <div className="min-w-0">
                      <p className="text-white truncate" title={a.message}>{a.message}</p>
                      {a.url && <p className="text-[9px] text-blue-400 truncate">{a.url}</p>}
                      <p className="text-[9px] text-gray-500">{new Date(a.created_at).toLocaleString("es-MX")}{a.expires_at ? ` → exp: ${new Date(a.expires_at).toLocaleString("es-MX")}` : ""}</p>
                    </div>
                    <span className={`text-[10px] uppercase font-bold ${a.type === "promo" ? "text-[#CCFF00]" : a.type === "warn" ? "text-yellow-400" : a.type === "success" ? "text-green-400" : "text-blue-400"}`}>{a.type}</span>
                    <Switch checked={!!a.active} onCheckedChange={() => toggleAnn(a)} />
                    <Button onClick={() => deleteAnn(a.id)} size="sm" variant="outline" className="border-red-500/30 text-red-400 h-7 w-7 p-0"><Trash size={12} /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* BACKFILL */}
        <TabsContent value="backfill" className="mt-3 space-y-3">
          <div className="card-tactical p-4 space-y-3">
            <p className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00]">Backfill retroactivo DYD</p>
            <p className="text-sm text-gray-300">
              Acredita DYD a TODOS los usuarios por su actividad histórica (matches validados, retas, torneos ganados, rentas pagadas, bono de registro)
              usando las reglas vigentes. <span className="text-[#CCFF00] font-bold">Es idempotente</span>: ejecutarlo varias veces no acredita dos veces.
            </p>
            <Button onClick={runBackfill} disabled={backfillRunning}
              className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase"
              data-testid="run-backfill">
              <ArrowsClockwise size={14} className={`mr-1 ${backfillRunning ? "animate-spin" : ""}`} />
              {backfillRunning ? "Procesando..." : "Ejecutar backfill"}
            </Button>
            {backfillResult && (
              <div className="bg-green-500/10 border border-green-500/30 p-3 rounded">
                <p className="text-sm text-green-300">{backfillResult.message}</p>
                <p className="text-[10px] text-gray-400 mt-1">Usuarios: {backfillResult.users_processed} · DYD acreditados: {backfillResult.total_credited}</p>
              </div>
            )}
          </div>
        </TabsContent>
        {/* ACTIVACIONES DE WALLET */}
        <TabsContent value="unlocks" className="mt-3 space-y-3">
          <div className="card-tactical p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Lock size={18} weight="fill" className="text-amber-400" />
              <h3 className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00]">Solicitudes de activación DYD pendientes ({unlocks.length})</h3>
            </div>
            <p className="text-[10px] text-gray-500">Los usuarios deben recargar el equivalente MXN al saldo DYD y subir comprobante. Tu trabajo: verificar el comprobante y aprobar/rechazar.</p>
            {unlocks.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">Sin solicitudes pendientes.</p>
            ) : (
              <div className="divide-y divide-white/5 border border-white/5 rounded overflow-hidden">
                {unlocks.map((u) => (
                  <div key={u.user_id} className="px-3 py-3 grid grid-cols-[1fr_110px_110px_220px] gap-3 items-center text-xs" data-testid={`unlock-row-${u.user_id}`}>
                    <div className="min-w-0">
                      <p className="text-white font-semibold truncate">{u.user?.name || "Usuario"}</p>
                      <p className="text-[10px] text-gray-500 truncate">{u.user?.email}</p>
                      <p className="text-[9px] text-gray-600">Enviado: {new Date(u.submitted_at).toLocaleString("es-MX")}</p>
                    </div>
                    <div className="text-center">
                      <p className="font-['Barlow_Condensed'] text-2xl font-black text-[#CCFF00] leading-none">{new Intl.NumberFormat("es-MX").format(u.balance)}</p>
                      <p className="text-[9px] text-gray-500 uppercase">DYD saldo</p>
                    </div>
                    <div className="text-center">
                      <p className="font-['Barlow_Condensed'] text-2xl font-black text-white leading-none">${new Intl.NumberFormat("es-MX").format(u.required_mxn)}</p>
                      <p className="text-[9px] text-gray-500 uppercase">MXN recarga</p>
                    </div>
                    <div className="flex items-center gap-1 justify-end flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => viewProof(u.user_id)} className="border-white/15 h-7 text-[10px]" data-testid={`view-proof-${u.user_id}`}>
                        <Eye size={12} className="mr-1" /> Comprobante
                      </Button>
                      <Button size="sm" onClick={() => approveUnlock(u.user_id)} className="bg-green-500 hover:bg-green-400 text-black font-bold h-7 text-[10px]" data-testid={`approve-${u.user_id}`}>
                        <CheckCircle size={12} className="mr-1" /> Aprobar
                      </Button>
                      <Button size="sm" onClick={() => rejectUnlock(u.user_id)} variant="outline" className="border-red-500/40 text-red-400 h-7 text-[10px]" data-testid={`reject-${u.user_id}`}>
                        <XCircle size={12} className="mr-1" /> Rechazar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {proofPreview && (
            <Dialog open={!!proofPreview} onOpenChange={() => setProofPreview(null)}>
              <DialogContent className="max-w-3xl bg-[#0A0A0A] border-[#CCFF00]/30">
                <DialogHeader><DialogTitle className="text-white">Comprobante bancario</DialogTitle></DialogHeader>
                {proofPreview.match(/\.pdf(\?|$)/i) ? (
                  <iframe src={proofPreview} title="proof" className="w-full h-[70vh] bg-white" />
                ) : (
                  <img src={proofPreview} alt="Comprobante" className="w-full max-h-[70vh] object-contain" />
                )}
                <a href={proofPreview} target="_blank" rel="noopener noreferrer" className="text-xs text-[#CCFF00] hover:underline">Abrir en nueva pestaña</a>
              </DialogContent>
            </Dialog>
          )}
        </TabsContent>

        {/* LEGAL — T&C, Privacy, Refund, Cookies */}
        <TabsContent value="legal" className="mt-3 space-y-3">
          <div className="card-tactical p-4">
            <div className="flex items-center gap-2 mb-2">
              <Scales size={18} weight="fill" className="text-[#CCFF00]" />
              <h3 className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00]">Documentos legales — Disfrutaydiviertete.com</h3>
            </div>
            <p className="text-xs text-gray-400 mb-4">Edita Términos & Condiciones, Aviso de Privacidad y demás textos legales que se muestran en la app y a usuarios al activar su wallet DYD. Las versiones se incrementan automáticamente.</p>

            {legalDocs.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">Cargando documentos...</p>
            ) : (
              <div className="space-y-2">
                {/* Group by category */}
                {["dyd", "general", "payments"].map((cat) => {
                  const inCat = legalDocs.filter((d) => d.category === cat);
                  if (inCat.length === 0) return null;
                  return (
                    <div key={cat}>
                      <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mt-3 mb-1">
                        {cat === "dyd" ? "DYD Moneda" : cat === "general" ? "General" : "Pagos"}
                      </p>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {inCat.map((doc) => (
                          <div key={doc.key} className="bg-[#0A0A0A] border border-white/10 p-3 rounded flex items-center justify-between gap-2" data-testid={`legal-${doc.key}`}>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm text-white font-semibold truncate">{doc.title}</p>
                              <p className="text-[10px] text-gray-500">v{doc.version} · {new Date(doc.updated_at).toLocaleDateString("es-MX")} · {doc.content?.length || 0} chars</p>
                            </div>
                            <Button size="sm" onClick={() => setEditingLegal({ ...doc })}
                              className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold h-7 text-[10px]"
                              data-testid={`edit-legal-${doc.key}`}>
                              Editar
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Editor modal */}
          {editingLegal && (
            <Dialog open={!!editingLegal} onOpenChange={(o) => !o && setEditingLegal(null)}>
              <DialogContent className="max-w-4xl bg-[#0A0A0A] border-[#CCFF00]/30 max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-white flex items-center gap-2">
                    <Scales size={20} className="text-[#CCFF00]" /> Editar: {editingLegal.title}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Título</Label>
                    <Input value={editingLegal.title}
                      onChange={(e) => setEditingLegal({ ...editingLegal, title: e.target.value })}
                      className="bg-[#0A0A0A] border-white/15 mt-1" maxLength={500}
                      data-testid="legal-edit-title" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">
                      Contenido <span className="text-gray-500">({editingLegal.content?.length || 0} / 100,000 chars)</span>
                    </Label>
                    <textarea
                      value={editingLegal.content}
                      onChange={(e) => setEditingLegal({ ...editingLegal, content: e.target.value })}
                      className="w-full mt-1 bg-[#0A0A0A] border border-white/15 rounded p-3 text-xs text-gray-200 font-mono leading-relaxed min-h-[400px] focus:outline-none focus:border-[#CCFF00]/40"
                      placeholder="Escribe aquí el texto legal completo..."
                      maxLength={100000}
                      data-testid="legal-edit-content"
                    />
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button onClick={saveLegal} disabled={savingLegal || !editingLegal.content?.trim()}
                      className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase"
                      data-testid="legal-save-btn">
                      <FloppyDisk size={14} className="mr-1" /> {savingLegal ? "Guardando..." : "Guardar y publicar"}
                    </Button>
                    <Button onClick={() => resetLegal(editingLegal.key)} variant="outline"
                      className="border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10"
                      data-testid="legal-reset-btn">
                      <ArrowCounterClockwise size={14} className="mr-1" /> Restaurar default
                    </Button>
                    <Button onClick={() => setEditingLegal(null)} variant="outline" className="border-white/15">
                      Cancelar
                    </Button>
                  </div>
                  <p className="text-[10px] text-gray-500">
                    💡 Tip: usa saltos de línea para separar párrafos. Los cambios se aplican inmediatamente — los usuarios verán la nueva versión al abrir el modal de activación o consultar legal.
                  </p>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </TabsContent>

        {/* DYD RUN — settings + challenges */}
        <TabsContent value="dyd-run" className="mt-3 space-y-3">
          <DydRunAdminPanel />
        </TabsContent>

        {/* REPORTS — RBAC */}
        <TabsContent value="reports" className="mt-3 space-y-3">
          <ReportsRbacPanel />
        </TabsContent>

        {/* THEME — color customization */}
        <TabsContent value="theme" className="mt-3 space-y-3">
          <ThemePanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DydRunAdminPanel() {
  const [settings, setSettings] = useState(null);
  const [perKm, setPerKm] = useState(5);
  const [challenges, setChallenges] = useState([]);
  const [form, setForm] = useState({
    name: "", description: "", type: "individual_km",
    target_km: 100, entry_fee_dyd: 50, seed_prize_dyd: 0,
    start_date: "", end_date: "",
  });

  const load = async () => {
    try {
      const [s, ch] = await Promise.all([
        api.get("/dyd-run/admin/settings"),
        api.get("/dyd-run/challenges"),
      ]);
      setSettings(s.data);
      setPerKm(s.data.dyd_per_km);
      setChallenges(ch.data.challenges || []);
    } catch (e) {
      toast.error("Error al cargar DYD Run");
    }
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    try {
      await api.put("/dyd-run/admin/settings", { dyd_per_km: Number(perKm) || 0 });
      toast.success(`Valor actualizado: 1 km = ${perKm} DYD`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const createChallenge = async () => {
    if (!form.name.trim()) { toast.error("Nombre requerido"); return; }
    if (!form.end_date) { toast.error("Fecha de fin requerida"); return; }
    try {
      await api.post("/dyd-run/admin/challenges", {
        ...form,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : undefined,
        end_date: new Date(form.end_date).toISOString(),
      });
      toast.success("Reto creado");
      setForm({ ...form, name: "", description: "", target_km: 100, entry_fee_dyd: 50, seed_prize_dyd: 0, start_date: "", end_date: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const closeChallenge = async (cid) => {
    if (!window.confirm("¿Cerrar el reto y repartir el premio?")) return;
    try {
      const r = await api.post(`/dyd-run/admin/challenges/${cid}/close`);
      toast.success(r.data.winner ? `Ganador: ${r.data.winner.user_name} (${r.data.winner.km} km)` : "Reto cerrado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const deleteChallenge = async (cid) => {
    if (!window.confirm("¿Cancelar reto? Se reembolsará la inscripción a los participantes.")) return;
    try {
      await api.delete(`/dyd-run/admin/challenges/${cid}`);
      toast.success("Reto cancelado y reembolsado");
      load();
    } catch (e) { toast.error("Error"); }
  };

  return (
    <div className="space-y-4">
      <div className="card-tactical p-4">
        <p className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00] mb-2 flex items-center gap-2">
          <Coin size={12} weight="fill" /> Valor del kilómetro
        </p>
        <p className="text-xs text-gray-400 mb-3">Cada corredor gana esta cantidad de DYD por cada km registrado (live, GPX o Strava).</p>
        <div className="flex items-end gap-3 max-w-md">
          <div className="flex-1">
            <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">DYD por km</Label>
            <Input type="number" min={0} max={1000} value={perKm}
              onChange={(e) => setPerKm(Number(e.target.value) || 0)}
              className="bg-[#0A0A0A] border-white/15 mt-1 font-mono text-xl"
              data-testid="dyd-per-km-input" />
            {settings && <p className="text-[10px] text-gray-500 mt-1">Por defecto: {settings.default} DYD/km</p>}
          </div>
          <Button onClick={saveSettings} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="save-dyd-per-km">
            <FloppyDisk size={14} className="mr-1" /> Guardar
          </Button>
        </div>
      </div>

      <div className="card-tactical p-4 space-y-3">
        <p className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00] flex items-center gap-2">
          <Trophy size={12} weight="fill" /> Crear reto grupal
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Nombre</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ej: Maratón 5K Marzo" className="bg-[#0A0A0A] border-white/15 mt-1" data-testid="ch-name" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Tipo</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/15 mt-1" data-testid="ch-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/15">
                <SelectItem value="individual_km">Individual (ganador = más km)</SelectItem>
                <SelectItem value="team_pool">Comunitario (todos suman a meta)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Descripción</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="¿De qué trata el reto?" className="bg-[#0A0A0A] border-white/15 mt-1" data-testid="ch-desc" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Inicio (vacío = ahora)</Label>
            <Input type="datetime-local" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              className="bg-[#0A0A0A] border-white/15 mt-1" data-testid="ch-start" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Fin *</Label>
            <Input type="datetime-local" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              className="bg-[#0A0A0A] border-white/15 mt-1" data-testid="ch-end" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Meta km (team_pool)</Label>
            <Input type="number" min={0} value={form.target_km} onChange={(e) => setForm({ ...form, target_km: Number(e.target.value) || 0 })}
              className="bg-[#0A0A0A] border-white/15 mt-1 font-mono" data-testid="ch-target" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Inscripción (DYD)</Label>
            <Input type="number" min={0} value={form.entry_fee_dyd} onChange={(e) => setForm({ ...form, entry_fee_dyd: Number(e.target.value) || 0 })}
              className="bg-[#0A0A0A] border-white/15 mt-1 font-mono" data-testid="ch-fee" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Premio semilla DYD (extra del organizador)</Label>
            <Input type="number" min={0} value={form.seed_prize_dyd} onChange={(e) => setForm({ ...form, seed_prize_dyd: Number(e.target.value) || 0 })}
              className="bg-[#0A0A0A] border-white/15 mt-1 font-mono" data-testid="ch-seed" />
            <p className="text-[10px] text-gray-500 mt-1">Premio total = semilla + (inscripción × participantes)</p>
          </div>
        </div>
        <Button onClick={createChallenge} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="ch-create">
          <Plus size={14} className="mr-1" /> Crear reto
        </Button>
      </div>

      <div className="card-tactical p-0 overflow-hidden">
        <h3 className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00] p-3">Retos activos ({challenges.length})</h3>
        {challenges.length === 0 ? (
          <p className="p-4 text-sm text-gray-500 italic">Sin retos activos.</p>
        ) : (
          <div className="divide-y divide-white/5">
            {challenges.map((c) => (
              <div key={c.id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`ch-row-${c.id}`}>
                <PersonSimpleRun size={20} className="text-[#CCFF00]" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white truncate">{c.name}</p>
                  <p className="text-[10px] text-gray-500">
                    {c.type === "team_pool" ? "Comunitario" : "Individual"} · {c.participant_count} participantes · Premio: {c.prize_pool_dyd} DYD
                    · Termina: {new Date(c.end_date).toLocaleString("es-MX")}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => closeChallenge(c.id)} data-testid={`ch-close-${c.id}`}>
                  Cerrar & pagar
                </Button>
                <Button size="sm" variant="ghost" onClick={() => deleteChallenge(c.id)} className="text-red-400" data-testid={`ch-del-${c.id}`}>
                  <Trash size={14} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function ReportsRbacPanel() {
  const ROLES = ["admin", "directivo", "profesor", "jugador"];
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/reports/access-config");
      setData(r.data);
    } catch (e) { toast.error("Error al cargar permisos"); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (section, role) => {
    if (!data) return;
    const cur = data.access[section] || [];
    const next = cur.includes(role) ? cur.filter((r) => r !== role) : [...cur, role];
    const newAccess = { ...data.access, [section]: next };
    try {
      const r = await api.put("/reports/access-config", { access: newAccess });
      setData({ ...data, access: r.data.access });
      toast.success(`${data.sections[section]}: ${role} ${cur.includes(role) ? "denegado" : "autorizado"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (!data) return <div className="text-sm text-gray-400">Cargando…</div>;

  return (
    <div className="card-tactical p-4 space-y-3">
      <p className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00] flex items-center gap-2">
        <Lock size={12} weight="fill" /> Quién puede generar cada reporte
      </p>
      <p className="text-xs text-gray-400">Admin siempre tiene acceso. Toggle por sección/rol — los cambios son inmediatos.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="rbac-table">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-2 px-3 text-[10px] uppercase tracking-widest text-gray-400">Apartado</th>
              {ROLES.map((r) => (
                <th key={r} className="py-2 px-2 text-[10px] uppercase tracking-widest text-gray-400 text-center">{r}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(data.sections).map((sec) => (
              <tr key={sec} className="border-b border-white/5 hover:bg-white/5">
                <td className="py-2 px-3 text-white font-bold">{data.sections[sec]}</td>
                {ROLES.map((role) => {
                  const checked = (data.access[sec] || []).includes(role) || role === "admin";
                  return (
                    <td key={role} className="py-2 px-2 text-center">
                      <button
                        onClick={() => role !== "admin" && toggle(sec, role)}
                        disabled={role === "admin"}
                        className={`w-8 h-8 inline-flex items-center justify-center border ${
                          checked
                            ? "bg-[#CCFF00] border-[#CCFF00] text-black"
                            : "border-white/15 text-gray-500 hover:border-[#CCFF00]/40"
                        } ${role === "admin" ? "opacity-70 cursor-default" : "cursor-pointer"}`}
                        data-testid={`rbac-${sec}-${role}`}
                      >
                        {checked ? <CheckCircle size={16} weight="fill" /> : <XCircle size={14} />}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ThemePanel() {
  const { theme: ctxTheme, defaultTheme, refresh, apply } = useTheme();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm(ctxTheme); }, [ctxTheme]);

  if (!form) return <div className="text-sm text-gray-400">Cargando…</div>;

  const update = (key, value) => {
    const next = { ...form, [key]: value };
    setForm(next);
    apply(next); // live preview
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/theme", form);
      toast.success("Tema actualizado — visible para todos");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm("¿Restaurar tema por defecto?")) return;
    try {
      await api.post("/theme/reset");
      setForm(defaultTheme);
      apply(defaultTheme);
      refresh();
      toast.success("Tema restaurado");
    } catch (e) { toast.error("Error"); }
  };

  const FIELDS = [
    { key: "primary", label: "Color primario (acento)", desc: "Botones, highlights, links" },
    { key: "primary_dark", label: "Primario hover", desc: "Hover del primario" },
    { key: "bg", label: "Fondo principal", desc: "Background base" },
    { key: "surface", label: "Superficie", desc: "Cards y paneles" },
    { key: "text", label: "Texto principal", desc: "Color del texto" },
    { key: "text_muted", label: "Texto secundario", desc: "Labels y descripciones" },
    { key: "danger", label: "Peligro", desc: "Errores y alertas" },
    { key: "success", label: "Éxito", desc: "Confirmaciones" },
  ];

  return (
    <div className="space-y-4">
      <div className="card-tactical p-4">
        <p className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00] mb-2 flex items-center gap-2">
          <Palette size={12} weight="fill" /> Colores del ecosistema
        </p>
        <p className="text-xs text-gray-400 mb-4">Los cambios se aplican en vivo. Guarda para que los demás los vean.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key} className="flex items-center gap-3 p-3 bg-[#0A0A0A] border border-white/10">
              <input
                type="color"
                value={form[f.key]?.startsWith("#") ? form[f.key] : "#000000"}
                onChange={(e) => update(f.key, e.target.value)}
                className="w-12 h-12 border-0 cursor-pointer"
                data-testid={`color-${f.key}`}
              />
              <div className="flex-1 min-w-0">
                <Label className="text-[10px] uppercase tracking-widest font-bold text-gray-400">{f.label}</Label>
                <Input
                  value={form[f.key]}
                  onChange={(e) => update(f.key, e.target.value)}
                  className="bg-transparent border-white/10 mt-1 font-mono text-xs"
                  data-testid={`color-input-${f.key}`}
                />
                <p className="text-[10px] text-gray-500 mt-1">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-4">
          <Button onClick={save} disabled={busy} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="btn-save-theme">
            <FloppyDisk size={14} className="mr-1" /> Guardar tema
          </Button>
          <Button onClick={reset} variant="outline" data-testid="btn-reset-theme">
            <ArrowCounterClockwise size={14} className="mr-1" /> Restaurar por defecto
          </Button>
        </div>
      </div>

      <div className="card-tactical p-4">
        <p className="text-[10px] tracking-widest uppercase font-bold text-[#CCFF00] mb-3">Vista previa</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div style={{ background: form.surface, border: `1px solid ${form.border}` }} className="p-4">
            <p style={{ color: form.text }} className="font-bold mb-1">Card sample</p>
            <p style={{ color: form.text_muted }} className="text-xs">Esto es un texto secundario.</p>
            <button style={{ background: form.primary, color: form.bg }} className="mt-3 px-3 py-1 text-xs font-bold uppercase">Botón</button>
          </div>
          <div style={{ background: form.bg, border: `1px solid ${form.border}` }} className="p-4">
            <p style={{ color: form.primary }} className="font-bold mb-1">Highlight</p>
            <p style={{ color: form.text_muted }} className="text-xs">Color primario sobre fondo principal.</p>
          </div>
          <div style={{ background: form.surface, border: `1px solid ${form.border}` }} className="p-4 space-y-2">
            <span style={{ background: form.success, color: "white" }} className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase">Éxito</span>
            <span style={{ background: form.danger, color: "white" }} className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase ml-2">Peligro</span>
          </div>
        </div>
      </div>
    </div>
  );
}
